import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as ExportIcon } from "../../../assets/icons/export.svg";
import { ReactComponent as SubmitIcon } from "../../../assets/icons/submit.svg";
import useReaderStore from "../../../context/readerStore";
import {
  executeAiActionStream,
  peekAskAiMessages,
  rememberAskAiMessages,
} from "../../../services/aiService";
import { showComingSoonNotification } from "../../../services/notificationService";
import AiGeneratingStatus from "../AiGeneratingStatus";
import AiMarkdown from "../AiMarkdown";
import AiResponseActions from "../AiResponseActions";
import { createStreamPainter } from "../streamPaint";

let messageId = 0;
const nextId = () => {
  messageId += 1;
  return `msg-${messageId}`;
};

const loadMessages = (documentId) =>
  peekAskAiMessages(documentId).filter(
    (message) => !message.pending && !message.streaming,
  );

const AskAiTab = () => {
  const { t } = useTranslation();
  const documentId = useReaderStore((s) => s.documentId);
  const askAiPrefillText = useReaderStore((s) => s.askAiPrefillText);
  const clearAskAiPrefillText = useReaderStore((s) => s.clearAskAiPrefillText);
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState(() => loadMessages(documentId));
  const [isSending, setIsSending] = useState(false);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const painterRef = useRef(null);

  // Persist for this document (component is keyed by documentId in the parent).
  useEffect(() => {
    if (documentId) rememberAskAiMessages(documentId, messages);
  }, [documentId, messages]);

  useEffect(() => {
    const onNewChat = () => {
      abortRef.current?.abort?.();
      abortRef.current = null;
      painterRef.current?.cancel?.();
      painterRef.current = null;
      setMessages([]);
      setQuestion("");
      setIsSending(false);
    };
    window.addEventListener("reader:ask-ai-new-chat", onNewChat);
    return () => {
      window.removeEventListener("reader:ask-ai-new-chat", onNewChat);
      abortRef.current?.abort?.();
      painterRef.current?.cancel?.();
    };
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!askAiPrefillText) return;
    setQuestion(askAiPrefillText);
    clearAskAiPrefillText();
    // Focus after prefill so user can edit/send immediately
    requestAnimationFrame(() => {
      inputRef.current?.focus?.();
    });
  }, [askAiPrefillText, clearAskAiPrefillText]);

  const sendQuestion = async (rawText) => {
    const text = rawText.trim();
    if (!text || isSending) return;

    abortRef.current?.abort?.();
    painterRef.current?.cancel?.();
    const controller = new AbortController();
    abortRef.current = controller;

    const userMessage = { id: nextId(), role: "user", content: text };
    const pendingId = nextId();
    // Drop any in-flight assistant bubble from a prior aborted request.
    setMessages((prev) => [
      ...prev.filter(
        (message) =>
          !(
            message.role === "assistant" &&
            (message.pending || message.streaming)
          ),
      ),
      userMessage,
      {
        id: pendingId,
        role: "assistant",
        content: t("readingAssistant.askAi.sending"),
        pending: true,
      },
    ]);
    setQuestion("");
    setIsSending(true);

    const painter = createStreamPainter((visible, isDone) => {
      setMessages((prev) =>
        prev.map((message) =>
          message.id === pendingId
            ? {
                ...message,
                content: visible,
                pending: false,
                streaming: !isDone,
                error: false,
              }
            : message,
        ),
      );
    });
    painterRef.current = painter;

    try {
      const data = await executeAiActionStream({
        action_type: "custom",
        prompt: text,
        content: "",
        signal: controller.signal,
        onUpdate: (partial) => {
          const answer = partial?.answer || "";
          if (!answer && !partial?.done) return;
          // Paint as soon as the first content delta arrives.
          painter.push(answer, Boolean(partial?.done));
        },
      });

      painter.push(data?.answer || "", true);
    } catch (error) {
      painter.cancel();
      if (error?.code === "ABORTED" && controller.signal.aborted) return;
      setMessages((prev) =>
        prev.map((message) =>
          message.id === pendingId
            ? {
                ...message,
                content: t("readingAssistant.askAi.error", {
                  message: error?.message || "",
                }),
                pending: false,
                streaming: false,
                error: true,
              }
            : message,
        ),
      );
    } finally {
      if (painterRef.current === painter) painterRef.current = null;
      if (abortRef.current === controller) abortRef.current = null;
      setIsSending(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendQuestion(question);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendQuestion(question);
    }
  };

  return (
    <div className="reading-assistant-tab-panel ask-ai-tab">
      <div
        className="ask-ai-messages"
        ref={messagesContainerRef}
        role="log"
        aria-live="polite"
      >
        {messages.length === 0 ? (
          <p className="ask-ai-empty">{t("readingAssistant.askAi.empty")}</p>
        ) : (
          messages.map((message) => {
            const showExport =
              message.role === "assistant" &&
              !message.pending &&
              !message.streaming &&
              !message.error &&
              Boolean(message.content?.trim());

            return (
              <div
                key={message.id}
                className={`ask-ai-row ask-ai-row-${message.role}`}
              >
                {message.role === "assistant" ? (
                  <div className="ask-ai-assistant-stack">
                    <div
                      className={`ask-ai-bubble ask-ai-bubble-assistant${
                        message.error ? " is-error" : ""
                      }${message.pending ? " is-pending" : ""}${
                        message.streaming ? " is-streaming" : ""
                      }`}
                      role={message.error ? "alert" : undefined}
                    >
                      {message.pending ? (
                        <AiGeneratingStatus message={message.content} />
                      ) : message.error ? (
                        message.content
                          .split("\n")
                          .map((line, index) => (
                            <p key={`${message.id}-${index}`}>{line}</p>
                          ))
                      ) : (
                        <>
                          <AiMarkdown>{message.content}</AiMarkdown>
                          {message.streaming ? (
                            <span
                              className="ai-stream-caret"
                              aria-hidden="true"
                            />
                          ) : null}
                        </>
                      )}
                    </div>
                    {showExport && (
                      <AiResponseActions
                        startActions={[
                          {
                            id: "export",
                            label: t("readingAssistant.askAi.export"),
                            onClick: () => showComingSoonNotification("export"),
                            icon: (
                              <ExportIcon
                                width={16}
                                height={16}
                                aria-hidden="true"
                              />
                            ),
                          },
                        ]}
                      />
                    )}
                  </div>
                ) : (
                  <div className="ask-ai-bubble ask-ai-bubble-user">
                    {message.content.split("\n").map((line, index) => (
                      <p key={`${message.id}-${index}`}>{line}</p>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="ask-ai-composer">
        <form
          className="ask-ai-input-shell ra-gradient-border"
          onSubmit={handleSubmit}
        >
          <textarea
            ref={inputRef}
            className="ask-ai-input"
            rows={2}
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("readingAssistant.askAi.placeholder")}
            aria-label={t("readingAssistant.askAi.placeholder")}
            disabled={isSending}
          />
          <button
            type="submit"
            className="ask-ai-submit"
            aria-label={t("readingAssistant.askAi.submit")}
            title={t("readingAssistant.askAi.submit")}
            disabled={!question.trim() || isSending}
          >
            <SubmitIcon aria-hidden="true" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default AskAiTab;
